/**
 * Nursing-home-bond (RAD) refund arithmetic.
 *
 * A RAD principal is fixed, but what the estate actually receives is adjusted both ways:
 * statutory interest accrues on the refund after death and any separately-held credit is
 * returned, while the provider withholds what the resident still owed plus (for residents
 * on the 1 Nov 2025 accommodation arrangements) an Aged Care Act 2024 retention.
 *
 * Mirrors `backend/src/matters/inventory-items/utils/nursing-home-bond.util.ts` — the two
 * packages share no code, so keep them in step when either changes.
 */

const ADDITIVE_KEYS = [
  'valueAtDateOfDeath',
  'heldCredit',
  'accruedInterest'
] as const;
const SUBTRACTIVE_KEYS = ['feesDeducted', 'retentionDeducted'] as const;

export const NURSING_HOME_BOND_COMPONENT_KEYS = [
  ...ADDITIVE_KEYS,
  ...SUBTRACTIVE_KEYS
] as const;

export type NursingHomeBondComponents = Partial<
  Record<(typeof NURSING_HOME_BOND_COMPONENT_KEYS)[number], unknown>
>;

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

/** Is any refund component recorded? Distinguishes a broken-down refund from a bare total. */
export function hasNursingHomeBondComponents(
  itemInfo: NursingHomeBondComponents
): boolean {
  return NURSING_HOME_BOND_COMPONENT_KEYS.some(
    (key) => asNumber(itemInfo[key]) !== undefined
  );
}

/**
 * The total the estate will collect, derived from the recorded components.
 *
 * `undefined` when no component is recorded — there is nothing to derive from, so a total
 * entered directly is left alone. Absent components count as zero.
 */
export function computeNursingHomeBondTotal(
  itemInfo: NursingHomeBondComponents
): number | undefined {
  if (!hasNursingHomeBondComponents(itemInfo)) return undefined;

  const total =
    ADDITIVE_KEYS.reduce(
      (sum, key) => sum + (asNumber(itemInfo[key]) ?? 0),
      0
    ) -
    SUBTRACTIVE_KEYS.reduce(
      (sum, key) => sum + (asNumber(itemInfo[key]) ?? 0),
      0
    );

  // Currency: keep to cents so repeated derivations don't accumulate float drift.
  return Math.round(total * 100) / 100;
}

/**
 * Does the recorded total disagree with what the components add up to?
 *
 * Drives the form's reconciliation hint. `false` when there is nothing to compare — no
 * components, or no total — so the hint never fires on a partially-filled bond.
 */
export function isNursingHomeBondTotalReconciled(
  itemInfo: NursingHomeBondComponents & { currentValue?: unknown }
): boolean | undefined {
  const derived = computeNursingHomeBondTotal(itemInfo);
  const recorded = asNumber(itemInfo.currentValue);
  if (derived === undefined || recorded === undefined) return undefined;
  // Tolerance of half a cent — the figures are currency, not floats to compare exactly.
  return Math.abs(derived - recorded) < 0.005;
}
